import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, Settings, CheckCheck } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/loader'
import { EmptyState } from '@/components/ui/empty-state'
import { NotificationRow } from './notification-row'
import { useNotifications } from '@/hooks/useNotifications'
import { useNotificationActions } from '@/hooks/useNotificationActions'
import { cn } from '@/lib/utils'
import { formatBadgeCount } from '@/lib/badge-count'

interface NotificationBellProps {
  className?: string
}

export function NotificationBell({ className }: NotificationBellProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const lang = i18n.language || 'nb'
  const {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    autoMarkVisibleAsRead,
    archive,
  } = useNotifications({ limit: 10, filter: 'all', notifyOnArrival: false })

  const [open, setOpen] = useState(false)
  const autoMarkTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open) {
      if (autoMarkTimer.current) {
        clearTimeout(autoMarkTimer.current)
        autoMarkTimer.current = null
      }
      return
    }
    const visibleUnreadIds = notifications.filter((n) => !n.read).map((n) => n.id)
    if (visibleUnreadIds.length === 0) return
    autoMarkTimer.current = setTimeout(() => {
      void autoMarkVisibleAsRead(visibleUnreadIds)
    }, 1500)
    return () => {
      if (autoMarkTimer.current) clearTimeout(autoMarkTimer.current)
    }
  }, [open, notifications, autoMarkVisibleAsRead])

  const { snoozeForHours, muteEvent } = useNotificationActions()

  const badgeLabel = formatBadgeCount(unreadCount)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('notifications.title', { defaultValue: 'Varsler' })}
          className={cn('relative', className)}
        >
          <Bell className="size-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--nav-badge-bg)] px-1 text-micro font-bold leading-none text-[var(--nav-badge-text)]"
              aria-label={t('notifications.unreadCount', { count: unreadCount })}
            >
              {badgeLabel}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] max-w-[calc(100vw-1rem)]">
        <header className="flex items-center justify-between gap-2 border-b border-[var(--border-default)] px-3 py-2">
          <div className="flex items-center gap-2">
            <Bell className="size-4 text-[var(--text-muted)]" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              {t('notifications.title', { defaultValue: 'Varsler' })}
            </h3>
            {unreadCount > 0 && (
              <Badge variant="action" size="sm">{badgeLabel}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => void markAllAsRead()}
              >
                <CheckCheck className="size-3.5" aria-hidden="true" />
                {t('notifications.markAllRead', { defaultValue: 'Merk alle som lest' })}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('notifications.settingsLink', { defaultValue: 'Innstillinger' })}
              onClick={() => {
                setOpen(false)
                navigate('/settings/notifications')
              }}
            >
              <Settings className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </header>

        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={Bell}
                title={t('notifications.empty', { defaultValue: 'Ingen varsler' })}
                description={t('notifications.emptyDesc', {
                  defaultValue: 'Vi varsler deg her når noe skjer – påmeldinger, kunngjøringer, tilbud og chat.',
                })}
              />
            </div>
          ) : (
            <ul className="divide-y divide-[var(--border-default)]">
              {notifications.map((n) => (
                <li key={n.id}>
                  <NotificationRow
                    notification={n}
                    locale={lang}
                    onMarkRead={markAsRead}
                    onMarkUnread={markAsUnread}
                    onArchive={archive}
                    onSnooze={snoozeForHours}
                    onMuteEvent={muteEvent}
                    onClose={() => setOpen(false)}
                    compact
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="border-t border-[var(--border-default)] p-2">
          <Button
            variant="ghost"
            size="icon"
            asChild
            className="w-full justify-center"
            onClick={() => setOpen(false)}
          >
            <Link to="/notifications">
              {t('notifications.viewAll', { defaultValue: 'Se alle varsler' })}
            </Link>
          </Button>
        </footer>
      </PopoverContent>
    </Popover>
  )
}
